import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { IOverlayTypeState, OverlayContext } from 'context/Overlay';
import { IOverlayProps } from './types';
import { SCModalWrapper, SCSpinnerWrapper } from 'styles/global-styles';
import { AuthenticationSessionManagement } from 'components/organisms/AuthenticationSessionManagement';
import { getQueryVariables } from 'utils/routeUtilities';
import { EConsentDisclaimer } from 'components/atoms/EConsentDisclaimer';
import { OverlayDisplayTypesEnum, OverlayTypesEnum } from 'enums/OverlayTypes';
import { ClientContextsService } from 'api/ClientContextsService';
import { SCOverlayWrapper } from 'components/organisms/Overlay/styles';
import { TSDialog } from 'components/atoms/TSDialog';
import { DistributorSelector } from 'components/organisms/DistributorSelector';
import { BeforeUnload } from 'components/molecules/BeforeUnload';
import { CircularProgress } from '@mui/material';
import { LocalStorageKeys } from 'enums/LocalStorageKeys';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Routes } from 'routes';
import { CertificateStateContext, ICertificateStateTypeState } from 'context/CertificateState';
import { LocationDescriptor } from 'history';
import { SessionStateEnums } from 'enums/SessionStorageEnums';
import { DistributorDataContext, IDistributorDataTypeState } from 'context/DistributorData';
import { replaceValueInNestedObject } from 'utils/helpers/helpers';
import { FeatureFlagPartnersContext, IFeatureFlagPartnersTypeState } from 'context/FeatureFlagPartners';
import { PartnersService } from 'api/PartnersService';
import { IPartner, IPartnersApiResponse } from 'types/partners';
import { ILandingPagePartnerOptions } from 'types/distributor-information';
const Overlay = ({ children }: IOverlayProps): ReactElement => {
    const { contextStateDistributorData, updateContextStateDistributorData } = React.useContext(
        DistributorDataContext
    ) as IDistributorDataTypeState;
    const { updateContextStateCertificateState } = useContext(CertificateStateContext) as ICertificateStateTypeState;
    const { contextStateOverlay, updateContextStateOverlay } = React.useContext(OverlayContext) as IOverlayTypeState;
    const { contextStateFeatureFlagPartners, updateContextStateFeatureFlagPartners } = React.useContext(
        FeatureFlagPartnersContext
    ) as IFeatureFlagPartnersTypeState;
    const [isLoading, setIsLoading] = useState(false);
    const [modalEDeliveryState, setModalEDeliveryState] = useState<boolean>(false);
    const queryVariables = getQueryVariables();
    const history = useHistory();
    const routeMatchRoot = useRouteMatch({
        path: Routes.ROOT_PRETTY,
        exact: true
    });
    const routeMatchClaim = useRouteMatch({
        path: Routes.CLAIM_PRETTY,
        exact: true
    });
    const routeMatchClaimLookUp = useRouteMatch({
        path: Routes.CLAIM_LOOKUP_PRETTY,
        exact: true
    });
    const routeMatchFAQ = useRouteMatch({
        path: Routes.FAQS_PRETTY,
        exact: true
    });
    const routeMatchCertificate = useRouteMatch({
        path: Routes.CERTIFICATE_OF_INSURANCE_PRETTY,
        exact: true
    });
    const getPartners = async (): Promise<void> => {
        const data = (await PartnersService()) as IPartnersApiResponse;
        if (data?.payload) {
            const finalOptions: ILandingPagePartnerOptions[] = data.payload?.map(
                (val: IPartner): ILandingPagePartnerOptions => {
                    return {
                        label: val.name,
                        distributorKey: val.distributorKey,
                        loanServicerKey: val.loanServicerKey,
                        image: undefined
                    };
                }
            );
            updateContextStateFeatureFlagPartners(finalOptions);
        }
    };
    const getClientConfiguration = async (distributorKey: string, loanServicerKey: string): Promise<void> => {
        sessionStorage.setItem(SessionStateEnums.ACTIVE_REQUEST, JSON.stringify(true));
        setIsLoading(true);
        await ClientContextsService(distributorKey, loanServicerKey)
            .then((value: unknown | any) => {
                localStorage.removeItem(LocalStorageKeys.DISTRIBUTOR_KEY);
                localStorage.removeItem(LocalStorageKeys.LOAN_SERVICER_KEY);
                if (distributorKey) localStorage.setItem(LocalStorageKeys.DISTRIBUTOR_KEY, distributorKey as string);
                if (loanServicerKey)
                    localStorage.setItem(LocalStorageKeys.LOAN_SERVICER_KEY, loanServicerKey as string);
                replaceValueInNestedObject(
                    value?.payload,
                    '__LAUNCH_DATE__',
                    value?.payload.client_content.distributor_launch_date
                );
                updateContextStateDistributorData(value.payload);
            })
            .catch(() => {
                resetPartnerSelection();
            });
        setIsLoading(false);
        sessionStorage.setItem(SessionStateEnums.ACTIVE_REQUEST, JSON.stringify(false));
    };
    const resetPartnerSelection = (): void => {
        localStorage.removeItem(LocalStorageKeys.CLIENT_KEY); //deprecated key <- delete this line later
        localStorage.removeItem(LocalStorageKeys.DISTRIBUTOR_KEY);
        localStorage.removeItem(LocalStorageKeys.LOAN_SERVICER_KEY);
        updateContextStateOverlay({ type: OverlayTypesEnum.TAKEOVER, reviewState: true });
    };
    useEffect(() => {
        contextStateDistributorData.distributorKey &&
            contextStateDistributorData.distributorKey?.length > 0 &&
            updateContextStateOverlay({ type: undefined, reviewState: false });
    }, [contextStateDistributorData]);
    useEffect(() => {
        const currentParamDID = routeMatchRoot?.params['did'];
        const currentParamDIDClaim = routeMatchClaim?.params['did'];
        const currentParamDIDClaimLookUp = routeMatchClaimLookUp?.params['did'];
        const currentParamDIDFAQ = routeMatchFAQ?.params['did'];
        const currentParamDIDCertificate = routeMatchCertificate?.params['did'];
        const isMatchRoot: boolean = contextStateFeatureFlagPartners.some(
            (obj: { distributorKey: string }) => obj.distributorKey === currentParamDID
        );
        const isMatchClaim: boolean = contextStateFeatureFlagPartners.some(
            (obj: { distributorKey: string }) => obj.distributorKey === currentParamDIDClaim
        );
        const isMatchClaimLookUp: boolean = contextStateFeatureFlagPartners.some(
            (obj: { distributorKey: string }) => obj.distributorKey === currentParamDIDClaimLookUp
        );
        const isMatchFAQ: boolean = contextStateFeatureFlagPartners.some(
            (obj: { distributorKey: string }) => obj.distributorKey === currentParamDIDFAQ
        );
        const isMatchCertificate: boolean = contextStateFeatureFlagPartners.some(
            (obj: { distributorKey: string }) => obj.distributorKey === currentParamDIDCertificate
        );
        const routeMatchAndInitConfiguration = (
            routeValid: object | null,
            routeMatchDID: boolean,
            currentParamDID: string,
            route: LocationDescriptor<unknown>
        ): void => {
            routeValid &&
                routeMatchDID &&
                getClientConfiguration(currentParamDID, '')
                    .then(() => {
                        contextStateDistributorData.distributorKey &&
                            contextStateDistributorData.distributorKey?.length > 0 &&
                            updateContextStateOverlay({ type: undefined, reviewState: false });
                        routeValid && history.push(route);
                        routeMatchCertificate && updateContextStateCertificateState({ certificateView: true });
                    })
                    .catch(() => {
                        resetPartnerSelection();
                    });
        };
        isMatchRoot && routeMatchAndInitConfiguration(routeMatchRoot, isMatchRoot, currentParamDID, Routes.HOME);
        isMatchClaim &&
            routeMatchAndInitConfiguration(routeMatchClaim, isMatchClaim, currentParamDIDClaim, Routes.CLAIM);
        isMatchFAQ && routeMatchAndInitConfiguration(routeMatchFAQ, isMatchFAQ, currentParamDIDFAQ, Routes.FAQS);
        isMatchClaimLookUp &&
            routeMatchAndInitConfiguration(
                routeMatchClaimLookUp,
                isMatchClaimLookUp,
                currentParamDIDClaimLookUp,
                Routes.CLAIM_LOOKUP
            );
        isMatchCertificate &&
            routeMatchAndInitConfiguration(
                routeMatchCertificate,
                isMatchCertificate,
                currentParamDIDCertificate,
                Routes.HOME
            );
    }, [contextStateDistributorData.distributorKey, contextStateFeatureFlagPartners]);
    useEffect(() => {
        window.addEventListener('error', (e) => {
            if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
                const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
                const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute('style', 'display: none');
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute('style', 'display: none');
                }
            }
        });
    }, [window]);
    useEffect(() => {
        const localStorageDistributorKey = localStorage.getItem(LocalStorageKeys.DISTRIBUTOR_KEY);
        const localStorageLoanServicerKey = localStorage.getItem(LocalStorageKeys.LOAN_SERVICER_KEY);
        const activeRequest = JSON.parse(sessionStorage.getItem(SessionStateEnums.ACTIVE_REQUEST) as string);
        const queryContainsDistributor =
            queryVariables &&
            contextStateFeatureFlagPartners.some(
                (featureFlagPartner) => featureFlagPartner.distributorKey === queryVariables['did']
            );
        const localStorageContainsDistributor =
            localStorageDistributorKey &&
            contextStateFeatureFlagPartners.some(
                (featureFlagPartner) => featureFlagPartner.distributorKey === localStorageDistributorKey
            );
        if (queryVariables && queryVariables['displayType'] === OverlayDisplayTypesEnum.E_CONSENT_LEGAL) {
            setModalEDeliveryState(true);
        }
        if (
            !!queryVariables &&
            !queryContainsDistributor &&
            localStorageContainsDistributor &&
            !activeRequest &&
            contextStateFeatureFlagPartners.length > 1
        ) {
            resetPartnerSelection();
        } else if (
            !queryVariables &&
            localStorageContainsDistributor &&
            !activeRequest &&
            contextStateFeatureFlagPartners.length > 1
        ) {
            const lsIdQueryParam = localStorageLoanServicerKey ? localStorageLoanServicerKey : '';
            localStorageDistributorKey && getClientConfiguration(localStorageDistributorKey, lsIdQueryParam);
        } else if (
            !!queryVariables &&
            queryContainsDistributor &&
            !activeRequest &&
            contextStateFeatureFlagPartners.length > 1
        ) {
            const lsIdQueryParam = localStorageLoanServicerKey ? localStorageLoanServicerKey : '';
            getClientConfiguration(queryVariables['did'], lsIdQueryParam);
        }
    }, [contextStateFeatureFlagPartners.length]);
    useEffect(() => {
        getPartners();
        const localStorageDistributorKey = localStorage.getItem(LocalStorageKeys.DISTRIBUTOR_KEY);
        const localStorageLoanServicerKey = localStorage.getItem(LocalStorageKeys.LOAN_SERVICER_KEY);
        const activeRequest = JSON.parse(sessionStorage.getItem(SessionStateEnums.ACTIVE_REQUEST) as string);
        const containsDistributor =
            queryVariables &&
            contextStateFeatureFlagPartners.some(
                (featureFlagPartner) => featureFlagPartner.distributorKey === queryVariables['did']
            );
        if (
            !activeRequest &&
            localStorageDistributorKey &&
            containsDistributor &&
            contextStateFeatureFlagPartners.length > 1
        ) {
            getClientConfiguration(localStorageDistributorKey as string, localStorageLoanServicerKey as string).then(
                () => {}
            );
        } else if (!activeRequest && !localStorage.getItem(LocalStorageKeys.DISTRIBUTOR_KEY)) {
            resetPartnerSelection();
        }
    }, []);
    // console.info(
    //     '\n::::::::::::::::::::::::::::::::Overlay:::::::::::::::::::::::::::::::::',
    //     '\n::queryVariables::',
    //     queryVariables,
    //     '\n::contextStateOverlay::',
    //     contextStateOverlay,
    //     '\n::contextStateDistributorData::',
    //     contextStateDistributorData,
    //     '\n::contextStateFeatureFlagPartners::',
    //     contextStateFeatureFlagPartners,
    //     '\n::contextStateFeatureFlagPartners.length > 1::',
    //     contextStateFeatureFlagPartners.length > 1,
    //     '\n::localStorage.getItem(LocalStorageKeys.DISTRIBUTOR_KEY)::',
    //     localStorage.getItem(LocalStorageKeys.DISTRIBUTOR_KEY),
    //     '\n:::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::'
    // );

    return (
        <SCOverlayWrapper>
            {(!contextStateOverlay['reviewState'] && !isLoading && children) ||
                (contextStateOverlay['reviewState'] &&
                    contextStateOverlay['type'] === OverlayTypesEnum.TAKEOVER &&
                    contextStateFeatureFlagPartners.length > 1 &&
                    !isLoading && <DistributorSelector></DistributorSelector>)}
            {modalEDeliveryState && (
                <SCModalWrapper>
                    <TSDialog
                        ariaLabel="econsent-dialog"
                        id="econsent-dialog"
                        closeButtonText="Done"
                        open={modalEDeliveryState}
                        fullscreen={true}
                        handleClose={(): void => {
                            setModalEDeliveryState(false);
                        }}
                    >
                        <EConsentDisclaimer />
                    </TSDialog>
                </SCModalWrapper>
            )}
            {isLoading && (
                <SCSpinnerWrapper>
                    <CircularProgress
                        sx={{
                            color: (theme): string => {
                                return theme.palette.common.black;
                            },
                            animationDuration: '1550ms'
                        }}
                        size={100}
                        thickness={4}
                    />
                </SCSpinnerWrapper>
            )}
            <AuthenticationSessionManagement />
            <BeforeUnload
                isLoggedIn={false}
                nativeDialogue={false}
                customDialogue={false}
                modalEnabled={false}
                onContinue={(): void => {}}
                allowedRoutes={[]}
                onCancel={(): void => {}}
                beforeUnloadExecute={(): void => {
                    sessionStorage.removeItem(SessionStateEnums.ACTIVE_REQUEST);
                }}
            />
        </SCOverlayWrapper>
    );
};

export { Overlay };
